import { createContext, useContext, useMemo, PropsWithChildren } from 'react'
import { useQuery } from '@apollo/client/react'
import type { ApolloUseQueryResult } from '@vivaldis/graphql'
import { useUser } from '@vivaldis/session'
import { companyInformationQuery } from '../../graphql/queries/companyInformationQuery'
import {
  CompanyInformationQuery,
  CompanyInformationQuery_user_company_vivaldisOffice,
  CompanyInformationQueryVariables
} from '../../graphql/queries/__generated__/CompanyInformationQuery'
import { CompanyFeaturesEnum } from '../../typings/__generated__'

export type CompanyInformationQueryContextValue = ApolloUseQueryResult<
  CompanyInformationQuery,
  CompanyInformationQueryVariables,
  'empty' | 'complete' | 'streaming',
  CompanyInformationQueryVariables
>

export const CompanyInformationQueryContext = createContext<
  CompanyInformationQueryContextValue | undefined
>(undefined)

export function useCompanyInformationQuery(): CompanyInformationQueryContextValue {
  return useContext(CompanyInformationQueryContext)!
}

export function useCompanySettingsFeatures(): CompanyFeaturesEnum[] {
  const { data, previousData } = useCompanyInformationQuery()

  return useMemo(() => {
    // Note: During changing selecting company here is a short moment when `data` is `undefined`, so we relay on `previousData` to prevent menu item from flickering
    return (data || previousData)?.companySettings || []
  }, [data, previousData])
}

export function useCompanyHasWeatherObstructionsEnabled(): boolean {
  const companySettingsFeatures = useCompanySettingsFeatures()

  return useMemo(
    () =>
      companySettingsFeatures.some(
        companySetting =>
          companySetting === CompanyFeaturesEnum.WEATHER_OBSTRUCTIONS
      ) || false,
    [companySettingsFeatures]
  )
}

export function useCompanyHasDimonaAttestationEnabled(): boolean {
  const companySettingsFeatures = useCompanySettingsFeatures()

  return useMemo(
    () =>
      companySettingsFeatures.some(
        companySetting =>
          companySetting === CompanyFeaturesEnum.DIMONA_ATTESTATION
      ) || false,
    [companySettingsFeatures]
  )
}

export function useCompanyHasTeam(): boolean {
  const result = useCompanyInformationQuery()

  const hasActiveTeam = !!result?.data?.team?.edges?.length
  const hasDisabledTeam = !!result?.data?.teamWithDisabled?.edges?.length

  return hasActiveTeam || hasDisabledTeam
}

export function useCompanyHasContracts(): boolean {
  const result = useCompanyInformationQuery()

  return !!result?.data?.lastContractDate
}

export function useCompanyHasSurveys(): boolean {
  const result = useCompanyInformationQuery()

  return !!result?.data?.companySurveys?.edges?.length
}

export function useCompanyHasPositions(): boolean {
  const result = useCompanyInformationQuery()

  return !!result?.data?.positions?.edges?.length
}

export function useVivaldisOffice():
  | CompanyInformationQuery_user_company_vivaldisOffice
  | undefined
  | null {
  const result = useCompanyInformationQuery()

  return result?.data?.user?.company?.vivaldisOffice
}

export const CompanyInformationQueryProvider = ({
  children
}: PropsWithChildren<unknown>) => {
  const user = useUser()
  const currentCompanyId = useMemo(() => user?.company?.id, [user?.company?.id])

  const result = useQuery(companyInformationQuery, {
    skip: !currentCompanyId,
    variables: {
      companyId: String(currentCompanyId)
    }
  })

  return (
    <CompanyInformationQueryContext.Provider value={result}>
      {children}
    </CompanyInformationQueryContext.Provider>
  )
}
